import { __ } from '../i18n/vsme';
import { extractHubErrorFromPayload, hubApiFallbackMessage } from '../utils/hubApiError';
import type {
  ConnectorFilesQuery,
  ConnectorFilesResponse,
  ConnectorsResponse,
  DeleteResult,
  DepositResult,
  FileConnector,
  FileLabels,
  FileListQuery,
  FileListResponse,
  FileStoreError,
  StoredFile,
  UsagesResponse,
} from './fileStoreTypes';

export type ImportBlockedHint = { metric_key?: string; reason_code?: string; message?: string };

/** What the profile import answers; every part may be missing. */
export type ArtifactImportResult = {
  summary?: { conflicting_field_count?: number; observations_touched?: number };
  stable?: Record<string, unknown>;
  monthly_facts?: unknown[];
  annual_facts?: unknown[];
  blocked?: ImportBlockedHint[];
};

export type PrefillDivergence = { field_id?: string; imported_value?: unknown; metric_key?: string };

export type PrefillMergeResult = {
  merged_field_ids?: string[];
  skipped_non_empty?: number;
  skipped_locked?: number;
  skipped_pristine_imported?: number;
  divergences?: PrefillDivergence[];
  prefill_unavailable?: boolean;
};

export type FileStoreApiConfig = {
  /** The `accounting-hub/v1` REST base, with or without trailing slash. */
  apiBase: string;
  /** A `wp_rest` nonce: every request sends it, GET included (a cookie request without it is a 401). */
  nonce: string;
};

export type FileStoreApi = {
  list: (ownerId: number, query: FileListQuery) => Promise<FileListResponse>;
  deposit: (ownerId: number, file: File, labels: FileLabels) => Promise<DepositResult>;
  patch: (ownerId: number, fileId: number, labels: Partial<FileLabels>) => Promise<StoredFile>;
  remove: (ownerId: number, fileId: number) => Promise<DeleteResult>;
  usages: (ownerId: number, fileId: number) => Promise<UsagesResponse>;
  downloadBlob: (ownerId: number, fileId: number, inline: boolean) => Promise<Blob>;
  downloadUrl: (ownerId: number, fileId: number, inline: boolean) => string;
  /** Connectors the owner may import from; the route needs the deposit right (403 otherwise). */
  connectors: (ownerId: number) => Promise<FileConnector[]>;
  connectorFiles: (ownerId: number, connectionId: string, query: ConnectorFilesQuery) => Promise<ConnectorFilesResponse>;
  /** Stores one remote file under `labels`; answers like a deposit (deduplicated included). */
  importFromConnector: (
    ownerId: number,
    connectionId: string,
    remoteId: string,
    labels: FileLabels,
  ) => Promise<DepositResult>;
  /** Reads a FEC/DSN artifact into the company profile (`POST company/{id}/profile/import/artifact/{artifactId}`). */
  importArtifact: (companyId: number, artifactId: number) => Promise<ArtifactImportResult>;
  /** Merges the profile into a draft VSME report (`POST {vsmeBase}reports/{id}/prefill-merge`); answers its `data`. */
  prefillMerge: (vsmeBase: string, reportId: number) => Promise<PrefillMergeResult>;
};

export class FileStoreApiError extends Error implements FileStoreError {
  readonly code: string;

  readonly status: number;

  readonly data?: Record<string, unknown>;

  constructor({ code, message, status, data }: FileStoreError) {
    super(message);
    this.name = 'FileStoreApiError';
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

export const isFileStoreError = (error: unknown): error is FileStoreApiError => error instanceof FileStoreApiError;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const sessionExpiredMessage = (): string => __('Session expirée. Rechargez la page.');

const errorFromResponse = (payload: unknown, status: number): FileStoreApiError => {
  const hubError = extractHubErrorFromPayload(payload, status, hubApiFallbackMessage(status));
  const data = isRecord(payload) && isRecord(payload.data) ? payload.data : undefined;

  return new FileStoreApiError({
    code: hubError.code ?? `http_${status}`,
    message: status === 401 ? sessionExpiredMessage() : hubError.message,
    status,
    data,
  });
};

/** Normalises anything thrown by a request (network failure included) into a FileStoreError. */
export const toFileStoreError = (error: unknown): FileStoreApiError => {
  if (isFileStoreError(error)) {
    return error;
  }

  return new FileStoreApiError({
    code: 'network_error',
    message: __('Le serveur est injoignable. Vérifiez votre connexion et réessayez.'),
    status: 0,
  });
};

const appendQuery = (url: string, params: URLSearchParams): string => {
  const query = params.toString();
  if (query === '') {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
};

const appendAll = (params: URLSearchParams, name: string, values: readonly (string | number)[] | undefined) => {
  values?.forEach((value) => params.append(`${name}[]`, String(value)));
};

const listParams = (query: FileListQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (query.search !== undefined && query.search !== '') {
    params.set('search', query.search);
  }
  if (query.page !== undefined) {
    params.set('page', String(query.page));
  }
  if (query.per_page !== undefined) {
    params.set('per_page', String(query.per_page));
  }
  appendAll(params, 'type', query.type);
  appendAll(params, 'year', query.year);
  // The route reads entity 0 as Siège social.
  appendAll(params, 'entity', query.entity?.map((entityId) => entityId ?? 0));
  appendAll(params, 'provenance', query.provenance);
  appendAll(params, 'wanted_type', query.wanted_type);
  appendAll(params, 'suggest_year', query.suggest_year);

  return params;
};

const pageParams = ({ search, page }: ConnectorFilesQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (search !== undefined && search !== '') {
    params.set('search', search);
  }
  if (page !== undefined) {
    params.set('page', String(page));
  }

  return params;
};

/** The import body: like a deposit, Siège social omits `entity_record_id`. */
const importBody = (remoteId: string, labels: FileLabels): string => JSON.stringify({
  remote_file_id: remoteId,
  type_key: labels.type_key,
  concerned_year: labels.concerned_year,
  ...(labels.entity_record_id === null ? {} : { entity_record_id: labels.entity_record_id }),
});

const depositForm = (file: File, labels: FileLabels): FormData => {
  const form = new FormData();
  form.append('file', file);
  form.append('type_key', labels.type_key);
  form.append('concerned_year', String(labels.concerned_year));
  if (labels.entity_record_id !== null) {
    form.append('entity_record_id', String(labels.entity_record_id));
  }

  return form;
};

export const createFileStoreApi = ({ apiBase, nonce }: FileStoreApiConfig): FileStoreApi => {
  const base = apiBase.endsWith('/') ? apiBase : `${apiBase}/`;
  const filesUrl = (ownerId: number): string => `${base}company-files/owners/${ownerId}/files`;
  const fileUrl = (ownerId: number, fileId: number): string => `${filesUrl(ownerId)}/${fileId}`;
  const connectorsUrl = (ownerId: number): string => `${base}company-files/owners/${ownerId}/connectors`;
  const connectorFilesUrl = (ownerId: number, connectionId: string): string => (
    `${connectorsUrl(ownerId)}/${encodeURIComponent(connectionId)}/files`
  );

  const send = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const headers = new Headers(init.headers);
    headers.set('X-WP-Nonce', nonce);
    const response = await fetch(url, { ...init, credentials: 'same-origin', headers });
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null);
      throw errorFromResponse(payload, response.status);
    }

    return response;
  };

  const sendJson = async <T>(url: string, init: RequestInit = {}): Promise<T> => {
    const response = await send(url, init);
    return (await response.json()) as T;
  };

  return {
    list: (ownerId, query) => sendJson<FileListResponse>(appendQuery(filesUrl(ownerId), listParams(query))),
    deposit: (ownerId, file, labels) => sendJson<DepositResult>(filesUrl(ownerId), {
      method: 'POST',
      body: depositForm(file, labels),
    }),
    patch: async (ownerId, fileId, labels) => {
      const response = await sendJson<{ file: StoredFile }>(fileUrl(ownerId, fileId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(labels),
      });
      return response.file;
    },
    remove: (ownerId, fileId) => sendJson<DeleteResult>(fileUrl(ownerId, fileId), { method: 'DELETE' }),
    usages: (ownerId, fileId) => sendJson<UsagesResponse>(`${fileUrl(ownerId, fileId)}/usages`),
    downloadBlob: async (ownerId, fileId, inline) => {
      const params = new URLSearchParams({ inline: inline ? '1' : '0' });
      const response = await send(appendQuery(`${fileUrl(ownerId, fileId)}/download`, params));
      return response.blob();
    },
    downloadUrl: (ownerId, fileId, inline) => {
      // A plain link cannot carry a header: the nonce travels as `_wpnonce`.
      const params = new URLSearchParams({ inline: inline ? '1' : '0', _wpnonce: nonce });
      return appendQuery(`${fileUrl(ownerId, fileId)}/download`, params);
    },
    connectors: async (ownerId) => {
      const response = await sendJson<Partial<ConnectorsResponse> | null>(connectorsUrl(ownerId));
      // An answer without the list offers no connector rather than breaking the picker.
      return Array.isArray(response?.connectors) ? response.connectors : [];
    },
    connectorFiles: (ownerId, connectionId, query) => sendJson<ConnectorFilesResponse>(
      appendQuery(connectorFilesUrl(ownerId, connectionId), pageParams(query)),
    ),
    importFromConnector: (ownerId, connectionId, remoteId, labels) => sendJson<DepositResult>(
      connectorFilesUrl(ownerId, connectionId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: importBody(remoteId, labels),
      },
    ),
    importArtifact: (companyId, artifactId) => sendJson<ArtifactImportResult>(
      `${base}company/${companyId}/profile/import/artifact/${artifactId}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),
    prefillMerge: async (vsmeBase, reportId) => {
      const vsme = vsmeBase.endsWith('/') ? vsmeBase : `${vsmeBase}/`;
      const response = await send(`${vsme}reports/${reportId}/prefill-merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      // A 2xx without a readable body still merged: report it as an empty result.
      const body: unknown = await response.json().catch(() => ({}));
      return isRecord(body) && isRecord(body.data) ? (body.data as PrefillMergeResult) : {};
    },
  };
};

export type DepositRow = {
  file: File;
  type_key: string;
  concerned_year: number | null;
  entity_record_id: number | null;
};

/** A remote file picked in a connector, waiting for its labels. */
export type ConnectorPick = {
  connectionId: string;
  remote_file_id: string;
  name: string;
};

export type ConnectorImportRow = ConnectorPick & {
  type_key: string;
  concerned_year: number | null;
  entity_record_id: number | null;
};

/** A labelled row to store: a local file to upload, or a remote file to import. */
export type StoreRow = DepositRow | ConnectorImportRow;

/** A stored row whose bytes the owner already held under other labels: the server kept those. */
export type KeptLabels = {
  file: StoredFile;
  /** The name the user deposited, which may differ from the stored file's. */
  depositedName: string;
  requested: FileLabels;
};

export type DepositAllResult<R extends StoreRow> = {
  ok: DepositResult[];
  /** The `ok` deposits answered `labels_differ`, with the labels the user asked for. */
  kept: KeptLabels[];
  failed: { row: R; error: FileStoreError }[];
};

/**
 * Stores rows one after the other, in order — uploads a local file, imports a connector file
 * (never in parallel: the server deduplicates by content and two concurrent stores of the same
 * bytes would race). A row that fails is reported, never thrown, so the caller can attach what
 * succeeded and keep the rest.
 */
export const depositAll = async <R extends StoreRow>(
  api: FileStoreApi,
  ownerId: number,
  rows: readonly R[],
): Promise<DepositAllResult<R>> => {
  const result: DepositAllResult<R> = { ok: [], kept: [], failed: [] };

  for (const row of rows) {
    if (row.type_key === '' || row.concerned_year === null) {
      result.failed.push({
        row,
        error: { code: 'labels_incomplete', message: __('Choisissez le type et l’année du fichier.'), status: 0 },
      });
      continue;
    }

    const labels: FileLabels = {
      type_key: row.type_key,
      concerned_year: row.concerned_year,
      entity_record_id: row.entity_record_id,
    };
    try {
      const deposit = 'file' in row
        ? await api.deposit(ownerId, row.file, labels)
        : await api.importFromConnector(ownerId, row.connectionId, row.remote_file_id, labels);
      result.ok.push(deposit);
      // The same bytes twice in one batch come back as the same stored file: announce it once.
      if (deposit.labels_differ === true && !result.kept.some((kept) => kept.file.id === deposit.file.id)) {
        result.kept.push({ file: deposit.file, depositedName: 'file' in row ? row.file.name : row.name, requested: labels });
      }
    } catch (error) {
      result.failed.push({ row, error: toFileStoreError(error) });
    }
  }

  return result;
};
